import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Mail, Phone, UserPlus, Search } from 'lucide-react';
import { crmApi } from '@/api/crm';
import { NouveauClientDialog } from './NouveauClientDialog';
import { Badge } from './_ui';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Répertoire clients/prospects du portail.
 * Lecture + création rapide via le dialog partagé. Édition avancée (RCCM/NCC/
 * adresse/pipeline) sur le back-office CRM (/crm).
 */
export const ClientsPage = () => {
  const [query, setQuery]   = useState('');
  const [showNew, setShowNew] = useState(false);

  const contactsQ = useQuery({
    queryKey: ['crm', 'contacts', 'commercial-page', query],
    queryFn:  () => crmApi.getContacts({ search: query || undefined, limit: 100 }),
    staleTime: 30_000,
  });
  const contacts = contactsQ.data?.data ?? [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Mes clients</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--tx-3, #6B7280)', fontSize: 13 }}>
            Répertoire clients et prospects — édition avancée sur le CRM
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          style={{
            padding: '10px 14px', borderRadius: 10, border: 'none',
            background: 'var(--pr-1, #1F4E79)', color: '#fff', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700,
            boxShadow: '0 4px 12px -4px rgba(31,78,121,0.4)',
            whiteSpace: 'nowrap',
          }}
        >
          <UserPlus size={14} /> Nouveau
        </button>
      </div>

      <div style={{ position: 'relative', marginBottom: 12 }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--tx-3, #6B7280)' }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher (nom, société…)"
          style={{
            width: '100%', padding: '8px 12px 8px 32px',
            border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 14,
          }}
        />
      </div>

      {showNew && (
        <NouveauClientDialog
          onClose={() => setShowNew(false)}
          onCreated={() => setShowNew(false)}
        />
      )}

      {contactsQ.isLoading && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>
      )}

      {!contactsQ.isLoading && contacts.length === 0 && (
        <div style={{ background: '#fff', border: '1px solid var(--bd-1)', borderRadius: 12, padding: 32, textAlign: 'center' }}>
          <Users size={40} style={{ color: 'var(--tx-3)', marginBottom: 8 }} />
          <p style={{ color: 'var(--tx-3)', margin: 0, fontSize: 14 }}>
            {query ? 'Aucun contact correspondant à ta recherche.' : 'Aucun contact enregistré. Clique Nouveau pour en créer un.'}
          </p>
        </div>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {contacts.map((c: any) => (
          <div key={c.id} style={{
            background: '#fff', border: '1px solid var(--bd-1)', borderRadius: 12, padding: 14,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {c.fullName}
                  {c.companyName && <span style={{ color: 'var(--tx-3)', fontWeight: 400 }}> · {c.companyName}</span>}
                </div>
                {c.pays && <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>{c.pays}</div>}
              </div>
              <Badge tone={c.contactType === 'prospect' ? 'warning' : 'success'}>
                {c.contactType === 'prospect' ? 'Prospect' : 'Client'}
              </Badge>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
              {c.phone && (
                <a href={`tel:${c.phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--pr-1)', textDecoration: 'none' }}>
                  <Phone size={13} /> {c.phone}
                </a>
              )}
              {c.email && (
                <a href={`mailto:${c.email}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--pr-1)', textDecoration: 'none' }}>
                  <Mail size={13} /> {c.email}
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
